import { useInfiniteQuery, useQuery, type InfiniteData } from "@tanstack/react-query";
import { queryKeys } from "@/app/query/query-keys";
import { unwrap } from "@/app/lib/api-client";
import type { MovementBucket, MovementRange } from "@shared/contracts";
import { movementsService } from "./movements.service";
import type { MovementSessionModel, MovementSessionPageModel } from "./movements.model";

/**
 * ALL THE SERVER STATE THIS FEATURE HAS — three reads and no writes.
 *
 * 🚨 THE PREFIX RELATIONSHIP IS THE INVALIDATION DESIGN, and this namespace is deliberately NOT
 * under `logs`. A movement's history is an aggregate ACROSS logs, so it goes stale when ANY
 * debrief touches that movement — but it is not a log, and nesting it would mean one
 * invalidation either refreshing far too much or leaving a chart showing a total that does not
 * include the sets the athlete has just done. `queryKeys.movements.all` is what the debrief
 * invalidates; see `logs.queries.ts`.
 */

/** The library — everything performed, ever. The screen filters it on the phone. */
export function useMovementLibrary() {
  return useQuery({
    queryKey: queryKeys.movements.list(),
    queryFn: async () => unwrap(await movementsService.library()),
  });
}

/**
 * ONE MOVEMENT'S WHOLE PICTURE, for one window.
 *
 * ⚠️ THE WINDOW IS IN THE KEY, which is the opposite of the cursor rule every list in this app
 * follows — and right for the same underlying reason. There, a cursor in the key fragments ONE
 * list across pages that no invalidation reaches coherently. Here there is no list: three
 * months and three years are two different ANSWERS, and sharing one entry would hand a
 * three-month chart to a screen whose chips say three years.
 *
 * `placeholderData` keeps the previous window on screen while the next one is in the air. On a
 * gym's wifi the alternative is the chart disappearing every time a chip is tapped, which reads
 * as the control breaking the screen.
 */
export function useMovementStats(movement: string, range: MovementRange, bucket: MovementBucket) {
  return useQuery({
    queryKey: queryKeys.movements.detail(movement, range, bucket),
    queryFn: async () => unwrap(await movementsService.stats(movement, range, bucket)),
    placeholderData: (previous) => previous,
  });
}

/**
 * WHAT THE ATHLETE ACTUALLY DID, DAY BY DAY — sets grouped by the session they happened in
 * (BUD-387).
 *
 * ⚠️ AN INFINITE QUERY, WITH THE CURSOR IN THE PAGE PARAMS AND NEVER IN THE KEY — the same rule
 * the history list and the notes list follow, and it bites here for the reason the registry
 * states: `queryKeys.movements.all` is what a debrief invalidates, and with a cursor in the key
 * it would reach page one and leave every page below it stale in place, showing sets from
 * before the session the athlete has just finished.
 *
 * ⚠️ IT IS NOT WINDOWED BY THE RANGE CHIP, unlike {@link useMovementStats}. The chip governs the
 * chart; a set an athlete performed does not stop having happened because the chart above it is
 * showing three months, and re-fetching the whole list on every chip tap would spend a round
 * trip to answer a question nobody asked.
 */
export function useMovementSessions(movement: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.movements.sessions(movement),
    queryFn: async ({ pageParam }) => unwrap(await movementsService.sessions(movement, pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** Every loaded page, flattened — what the list actually renders. */
export function flattenMovementSessions(
  data: InfiniteData<MovementSessionPageModel> | undefined,
): MovementSessionModel[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}
